import Component, { Topic } from 'component';
import { replaceContent, assert, uuid, toNum } from 'utils';
import { CART_DRAWER_CONTENT_ID, ReqSection, ENTER_KEY_IDENTIFIER } from 'constants';

export const LINE_ITEM_DATA_ATTR = 'data-line-item';
export const NOTES_TEXT_DATA_ATTR = 'data-cart-notes-text';
export const MODAL_ID_ATTR = 'modal-id';
export const NOTES_TRIGGER_DATA_ATTR = 'data-notes-trigger';
export const NOTES_OPENER_DATA_ATTR = 'data-notes-opener';

const SLATWALL_ATTR = 'data-slatwall';
const INSTALL_LINE_ATTR = 'data-install-line';
const LOADING_CLASS = 'cart-drawer--loading';
const NOTES_OPEN_CLASS = 'cart-drawer--notes-open';

customElements.define('x-cart-drawer', class extends Component {
  constructor() {
    super();
    this.$element.content = this.querySelector('[data-cart-drawer-element="content"]');
  }

  modal = null;
  TUID = null;
  notesText = null;
  triggerInteractionsHandler = this.handleTriggerInteractions.bind(this);
  triggers = [];

  get drawerRoot() {
    return this.$element.content || this;
  }

  render() {
    this.modal = document.getElementById(this.getAttribute(MODAL_ID_ATTR));
    assert(this.modal, 'Cart drawer is missing dialog element');

    this.on(this, 'change', this.handleCartChange);

    this.notesText = this.querySelector(`[${NOTES_TEXT_DATA_ATTR}]`);
    if (this.notesText) {
      this.setupCartNotesFlow();
    }

    this.sub(
      Topic.CART_UPDATE,
      ({ data, error, TUID }) => {
        if (this.TUID && this.TUID === TUID) {
          this.loading(false);
        }

        if (!data) return;

        const targetDoc = data.sections[ReqSection.CART_DRAWER];
        if (targetDoc && this.updateContent(targetDoc)) {
          if (data.openDrawer) {
            this.modal.open();
          }
          if (this.notesText) {
            this.setupCartNotesFlow();
          }
        }
      },
      { global: true }
    );

    this.sub(
      Topic.NEWSLETTER_POPUP_OPENED,
      () => this.modal.refreshLayer(),
      { global: true, once: true }
    );
  }

  loading(state) {
    this.classList.toggle(LOADING_CLASS, state);
  }

  publishCartChange(id, qty) {
    this.pub(Topic.CART_CHANGE, { TUID: null, id, qty });
  }

  async handleCartChange(e) {
    const { target } = e;
    if (!target) return;

    if (target.hasAttribute(NOTES_TEXT_DATA_ATTR)) {
      this.pub(Topic.CART_NOTES, target.value);
      return;
    }

    const lineItemKey = target.getAttribute(LINE_ITEM_DATA_ATTR);
    if (!lineItemKey) return;

    const value = toNum(target.value);
    if (value === null) {
      console.error('Invalid value for line item:', lineItemKey);
      return;
    }

    const lineInputs = this.drawerRoot.querySelectorAll(
      `[${LINE_ITEM_DATA_ATTR}="${lineItemKey}"]`
    );
    const isSlatLine = Array.from(lineInputs).some(el => el.hasAttribute(SLATWALL_ATTR));
    const isInstallLine = Array.from(lineInputs).some(el => el.hasAttribute(INSTALL_LINE_ATTR));

    this.TUID = uuid();
    this.pub(Topic.CART_CHANGE, { TUID: this.TUID, id: lineItemKey, qty: value });
    this.loading(true);

    if (isSlatLine) {
      this.syncInstallWithSlat(lineItemKey, value);
    }

    if (isInstallLine) {
      this.constrainInstallToSlat(target, lineItemKey, value);
    }
  }

  syncInstallWithSlat(slatLineItemKey, slatQty) {
    const installInput = this.drawerRoot.querySelector(
      `input[${LINE_ITEM_DATA_ATTR}][${INSTALL_LINE_ATTR}="true"]`
    );
    if (!installInput) return;

    const installKey = installInput.getAttribute(LINE_ITEM_DATA_ATTR);
    if (installKey && installKey !== slatLineItemKey) {
      this.publishCartChange(installKey, Math.max(0, slatQty));
    }
  }

  constrainInstallToSlat(installInput, installLineItemKey, installQty) {
    const slatInput = this.drawerRoot.querySelector(
      `input[${LINE_ITEM_DATA_ATTR}][${SLATWALL_ATTR}="true"]`
    );
    if (!slatInput) return;

    const slatQty = toNum(slatInput.value);
    if (slatQty !== null && installQty > slatQty) {
      installInput.value = slatQty;
      this.publishCartChange(installLineItemKey, slatQty);
    }
  }

  updateContent(updatedDoc) {
    const updatedContent = updatedDoc.getElementById(CART_DRAWER_CONTENT_ID);
    if (!updatedContent) {
      console.error('No cart drawer container within response section');
      return false;
    }
    replaceContent(this.$element.content, updatedContent, true);
    return true;
  }

  handleTriggerInteractions(e) {
    if (!(e instanceof KeyboardEvent && e.key === ENTER_KEY_IDENTIFIER) && !(e instanceof MouseEvent)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    this.classList.toggle(NOTES_OPEN_CLASS);

    if (e.currentTarget.hasAttribute(NOTES_OPENER_DATA_ATTR)) {
      this.notesText?.focus();
    }
  }

  setupCartNotesFlow() {
    this.cleanupTriggers();

    this.triggers = Array.from(
      this.querySelectorAll(`[${NOTES_TRIGGER_DATA_ATTR}]`)
    );

    this.triggers.forEach(el => {
      el.addEventListener('keypress', this.triggerInteractionsHandler);
      el.addEventListener('click', this.triggerInteractionsHandler);
    });
  }

  cleanupTriggers() {
    this.triggers.forEach(el => {
      el.removeEventListener('keypress', this.triggerInteractionsHandler);
      el.removeEventListener('click', this.triggerInteractionsHandler);
    });
    this.triggers = [];
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.cleanupTriggers();
  }
});